import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Avatar,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import {
  Person as PersonIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Message as MessageIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import MessageButton from '../components/MessageButton';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

const API_BASE = API_BASE_URL;

interface Profile {
  id: number;
  userId: number;
  profession: string;
  rank?: string;
  department?: string;
  currentLocation: string;
  preferredLocations: string[];
  qualifications?: string;
  majors?: string;
  cvUrl?: string;
  verificationStatus: string;
  createdAt: string;
  updatedAt: string;
  User: {
    id: number;
    name: string;
    email: string;
  };
}

const TransferHubProfileView: React.FC = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadProfile = async () => {
      if (!profileId) {
        setError('Profile ID is required');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/transferhub/profiles/${profileId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setProfile(data);
        } else if (res.status === 404) {
          setError('Profile not found');
        } else if (res.status === 401) {
          navigate('/login');
        } else {
          setError('Failed to load profile');
        }
      } catch (err) {
        setError('Failed to load profile');
        console.error('Profile load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [profileId, token, navigate]);

  const handleMessage = async () => {
    navigate('/transferhub/conversations');
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading profile...</Typography>
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Profile not found'}
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/transferhub/profiles')}
        >
          Back to Profiles
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/transferhub/profiles')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Profile Details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ mr: 3, bgcolor: 'primary.main', width: 80, height: 80 }}>
                  <PersonIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" gutterBottom>
                    {profile.User.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Chip 
                      label={profile.verificationStatus} 
                      color={profile.verificationStatus === 'verified' ? 'success' : 'default'}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Member since {new Date(profile.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <EmailIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    <Typography variant="body2">
                      {profile.User.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Professional Information */}
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <WorkIcon sx={{ mr: 1 }} />
                Professional Information
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Profession
                  </Typography>
                  <Typography variant="body1">
                    {profile.profession}
                  </Typography>
                </Grid>
                {profile.rank && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Rank/Level
                    </Typography>
                    <Typography variant="body1">
                      {profile.rank}
                    </Typography>
                  </Grid>
                )}
                {profile.department && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Department
                    </Typography>
                    <Typography variant="body1">
                      {profile.department}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Location Information */}
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationIcon sx={{ mr: 1 }} />
                Location Information
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Location
                  </Typography>
                  <Typography variant="body1">
                    {profile.currentLocation}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Preferred Locations
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {profile.preferredLocations.map((location, index) => (
                      <Chip key={index} label={location} size="small" />
                    ))}
                  </Box>
                </Grid>
              </Grid>

              {/* Education & Qualifications */}
              {(profile.qualifications || profile.majors) && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <SchoolIcon sx={{ mr: 1 }} />
                    Education & Qualifications
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {profile.qualifications && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Qualifications
                        </Typography>
                        <Typography variant="body1">
                          {profile.qualifications}
                        </Typography>
                      </Grid>
                    )}
                    {profile.majors && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Specializations/Majors
                        </Typography>
                        <Typography variant="body1">
                          {profile.majors}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {/* CV Download */}
              {profile.cvUrl && (
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Resume/CV
                  </Typography>
                  <Button 
                    variant="outlined" 
                    href={profile.cvUrl} 
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download CV
                  </Button>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Action Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              
              {profile.User.id !== user?.id && (
                token ? (
                  <MessageButton
                    receiverId={profile.User.id}
                    receiverName={profile.User.name}
                    variant="contained"
                    size="medium"
                    startIcon
                  />
                ) : (
                  <Button 
                    variant="contained" 
                    fullWidth
                    startIcon={<MessageIcon />}
                    onClick={handleMessage}
                    sx={{ mb: 2 }}
                  >
                    Send Message
                  </Button>
                )
              )}
              
              <Button 
                variant="outlined" 
                fullWidth
                onClick={() => navigate('/transferhub/profiles')}
              >
                Back to All Profiles
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransferHubProfileView;

